import { OrbitControls, PerformanceMonitor } from "@react-three/drei";
import { Canvas, invalidate, useThree } from "@react-three/fiber";
import React, { useCallback, useEffect, useRef, useState } from "react";
import "semantic-ui-css/semantic.min.css";
import "../App.css";
import Loader from "./Loader";
import Ceilings from "./threeDComponent/Ceilings";
import CompassValue from "./threeDComponent/CompassValue";
import DaylightFactor from "./threeDComponent/DaylightFactor";
import Doors from "./threeDComponent/Doors";
import ExternalWalls from "./threeDComponent/ExternalWalls";
import InternalWalls from "./threeDComponent/InternalWalls";
import PVData from "./threeDComponent/PVData";
import PVPanels from "./threeDComponent/PVPanels";
import Rooftop from "./threeDComponent/Rooftop";
import RoomEnergyData from "./threeDComponent/RoomEnergyData";
import Rooms from "./threeDComponent/Rooms";
import SceneGLTF from "./threeDComponent/SceneGLTF";
import SensorPlacement from "./threeDComponent/SensorPlacement";
import WindAlarmSensors from "./threeDComponent/WindAlarmSensors";
import WindAlarmWindows from "./threeDComponent/WindAlarmWindows";
import ShadingProgression from "./threeDComponent/ShadingProgression";
import SolarSensors from "./threeDComponent/SolarSensors";
import Stairs from "./threeDComponent/Stairs";
import Sunlight from "./threeDComponent/Sunlight";
import WindDirection from "./threeDComponent/WindDirection";
import WindRiskData from "./threeDComponent/WindRiskData";
import WindTurbine from "./threeDComponent/WindTurbine";
import { useSelector } from "react-redux";
import { windowEvents } from "./events";
import Proper3DRectSelector from "./objectSelection/Proper3DRectSelector";
import RectangleUI from "./objectSelection/RectangleUI";
import ObjectPlacer from "./objectPlacement/ObjectPlacer";
import MeshViewer from "./sensorAnalysis/MeshViewer";
import MainSceneWindow from "./threeDComponent/MainSceneWindow";
import { useEventSubscription } from "./CustomHooks";
import PVDesign3DComponents from "./threeDComponent/PVDesign3DComponents";
import PolygonPlacer from "./objectPlacement/PolygonPlacer";
import SoundEmission from "./threeDComponent/SoundEmission";

const CameraController = ({ orbitRef }) => {
  const { camera, gl } = useThree();
  const { cameraPosition, cameraTarget, defaultCameraPosition } = useSelector(
    (store) => store.sceneVar,
  );

  const isDrawingRect = useSelector(
    (state) => state.objectSelection.rectangleUI.isDrawing,
  );
  const isDrawActive = useSelector(
    (state) => state.objectSelection.isDrawActive,
  );

  const reset = useCallback(() => {
    if (!orbitRef.current) return;
    orbitRef.current.reset();
    camera.position.set(...defaultCameraPosition);
  }, [camera.position, defaultCameraPosition, orbitRef]);

  const disable = useCallback(() => {
    if (orbitRef.current) {
      orbitRef.current.enabled = false;
    }
  }, [orbitRef]);

  const enable = useCallback(() => {
    if (orbitRef.current) {
      orbitRef.current.enabled = true;
    }
  }, [orbitRef]);

  const toggle = useCallback(() => {
    if (orbitRef.current) {
      orbitRef.current.enabled = !orbitRef.current.enabled;
    }
  }, [orbitRef]);

  useEventSubscription(windowEvents.RESET_CAMERA, reset);

  useEffect(() => {
    if (cameraPosition) {
      camera.position.set(...cameraPosition);
    }
  }, [camera, cameraPosition]);

  useEventSubscription(windowEvents.DISABLE_ORBIT, disable, gl.domElement);
  useEventSubscription(windowEvents.ENABLE_ORBIT, enable, gl.domElement);
  useEventSubscription(windowEvents.TOGGLE_ORBIT, toggle, gl.domElement);

  return (
    <OrbitControls
      target={cameraTarget}
      enableDamping={true}
      dampingFactor={0.1}
      ref={orbitRef}
      enabled={!isDrawingRect && !isDrawActive}
      onChange={() => invalidate()}
    />
  );
};

const Convas = ({ isMinified }) => {
  const [dpr, setDpr] = useState(1.5);
  const orbitRef = useRef();

  return (
    <>
      <Loader isMinified={isMinified} />
      <RectangleUI />
      <Canvas
        frameloop="demand"
        name="mainscene"
        camera={{ up: [0, 1, 0] }}
        shadows
        dpr={dpr}
        style={{ position: "absolute", zIndex: 1 }}
        gl={{
          antialias: true,
          preserveDrawingBuffer: true,
          powerPreference: "high-performance",
        }}
        onCreated={({ gl }) => {
          const canvas = gl.domElement;
          canvas.addEventListener("webglcontextlost", (event) => {
            event.preventDefault();
          });
          canvas.addEventListener("webglcontextrestored", () => invalidate());
        }}
      >
        <CameraController orbitRef={orbitRef} />
        <PerformanceMonitor
          flipflops={3}
          onFallback={() => setDpr(1)}
          onIncline={() => setDpr(2)}
          onDecline={() => setDpr(1)}
        ></PerformanceMonitor>
        <ambientLight intensity={1} />
        <directionalLight position={[10, 10, 5]} intensity={3} />
        <directionalLight position={[-10, -10, -5]} intensity={3} />
        <ObjectPlacer orbitRef={orbitRef} />
        <MeshViewer orbitRef={orbitRef} />
        <Ceilings />
        <PVPanels />
        <InternalWalls />
        <ExternalWalls />
        <MainSceneWindow />
        <SceneGLTF />
        <Rooftop />
        <SolarSensors />
        <Rooms />
        <Doors />
        <Stairs />
        <WindDirection />
        <WindRiskData />
        <SensorPlacement />
        <WindAlarmSensors />
        <WindAlarmWindows />
        <CompassValue />
        <RoomEnergyData />

        <PVDesign3DComponents />
        <PolygonPlacer orbitRef={orbitRef} />
        <PVData />
        <ShadingProgression />
        <Sunlight />
        <DaylightFactor />

        <WindTurbine />

        <Proper3DRectSelector />

        <SoundEmission />
        {/* <Stats showPanel="{2}" /> */}
      </Canvas>
    </>
  );
};

export default React.memo(Convas);
